import { ComboBoxItem } from "@aderant/aderant-react-components";
import { HitStatus, QuickSearch, SearchVersion } from "aderant-conflicts-models";
import { HitStatusComboBox } from "./HitStatusComboBox";
import { hitStatusList } from "dataProviders/hitStatusList";
import { RootState } from "MyTypes";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { usePermissionsContext } from "state/selectors/appSelectors";
import { getReviewerControlledStatuses } from "state/selectors/searchSelectors";

export type HitStatusComboBoxWrapperProps = {
    id?: string;
    hitStatus: HitStatus;
    searchVersion?: SearchVersion | QuickSearch;
    onStatusChange?: (status: HitStatus) => void;
    disabled?: boolean;
    style?: React.CSSProperties;
    className?: string;
};

// Type guard to check if searchVersion is of type SearchVersion
const isSearchVersion = (version: SearchVersion | QuickSearch | undefined): version is SearchVersion => {
    return version !== undefined && !version.isQuickSearch;
};

export const HitStatusComboBoxWrapper = (props: HitStatusComboBoxWrapperProps) => {
    const { id, hitStatus, searchVersion, onStatusChange, disabled = false, style, className } = props;
    const permissions = usePermissionsContext();
    
    const reviewerStatuses = useSelector((state: RootState) => {
        return getReviewerControlledStatuses(state);
    });

    // State to manage dropdown options dynamically
    const [dropdownOptions, setDropdownOptions] = useState<ComboBoxItem<HitStatus>[]>([]);

    // Memoized validation of search version
    const validSearchVersion = isSearchVersion(searchVersion) ? searchVersion : undefined;

    // Effect to update dropdown options
    useEffect(() => {
        const options = hitStatusList(permissions, validSearchVersion, reviewerStatuses, hitStatus);
        setDropdownOptions(options);
    }, [permissions, reviewerStatuses, hitStatus, validSearchVersion]);

    // Check if there are actionable options (options other than current status)
    const hasActionableOptions = dropdownOptions.some(option => option.value !== hitStatus);

    // Callback to handle status changes
    const handleStatusChange = useCallback(
        (status: ComboBoxItem<HitStatus>) => {
            // Return if the change and the current hit status are the same
            if (status.value === hitStatus) {
                return;
            }

            // Trigger the status change callback
            if (onStatusChange && status.value !== undefined) {
                onStatusChange(status.value);
            }
        },
        [hitStatus, onStatusChange]
    );

    return (
        <HitStatusComboBox
            id={id}
            disabled={disabled || !hasActionableOptions}
            hitStatus={hitStatus}
            searchVersion={validSearchVersion}
            onItemSelected={handleStatusChange}
            hitStatusOptions={dropdownOptions}
            style={style}
            className={className}
        />
    );
};